/**
 * Reusable field validators + input wrappers — Wave R7.
 *
 * Use these instead of plain <input> for any field that needs strict
 * type-of-content validation. Keeps validation rules in ONE place so
 * the public enquiry, admission, internal CRM, HRMS onboarding, vendor
 * profiles and bulk-upload validators stay consistent.
 *
 * Rules:
 *   • NAME fields  — alphabets + space + . ' - only. No digits.
 *                    Examples allowed: "Rahul Sharma", "D'Souza", "Jean-Pierre", "M. Ramesh"
 *                    Examples blocked: "Rahul123", "1234", "@Rahul"
 *   • NUMBER       — digits only. Use for age, pincode, fee, salary.
 *                    For PHONE, use the dedicated PhoneInput component.
 *   • ALPHANUMERIC — letters + digits + common punctuation. Use for
 *                    addresses, document numbers, invoice numbers,
 *                    employee IDs, remarks (i.e. fields that NATURALLY
 *                    contain both letters and numbers).
 */
import { forwardRef } from "react";

// ─────────────────────────────────────────────────────────────────────
// Validators (pure functions — for form-level + bulk-upload validation)
// ─────────────────────────────────────────────────────────────────────

const NAME_REGEX = /^[\p{L}\p{M}][\p{L}\p{M}\s.'\-]{0,80}$/u;
// Allows: alphabets in any script (Devanagari, Latin, etc.) + combining
// marks (matras / vowel signs for Indic scripts), space, dot, apostrophe,
// hyphen. Must START with a letter or mark. Max 80 chars.

export function isValidName(value) {
  if (value == null || value === "") return false;
  return NAME_REGEX.test(String(value).trim());
}

export function nameError(value, { required = true, label = "name" } = {}) {
  const v = (value ?? "").toString().trim();
  if (!v) return required ? "Please enter the " + label + "." : "";
  if (/\d/.test(v)) {
    return `Please enter a valid ${label}. Numbers are not allowed.`;
  }
  if (!NAME_REGEX.test(v)) {
    return `Please enter a valid ${label}. Special characters are not allowed.`;
  }
  return "";
}

const NUMERIC_REGEX = /^\d+$/;

export function isNumeric(value) {
  if (value == null || value === "") return false;
  return NUMERIC_REGEX.test(String(value).trim());
}

export function numericError(value, { required = true, label = "value", min, max } = {}) {
  const v = (value ?? "").toString().trim();
  if (!v) return required ? `Please enter the ${label}.` : "";
  if (!NUMERIC_REGEX.test(v)) {
    return `${label} must contain only numbers.`;
  }
  const n = parseInt(v, 10);
  if (min != null && n < min) return `${label} must be at least ${min}.`;
  if (max != null && n > max) return `${label} must be at most ${max}.`;
  return "";
}

// ─────────────────────────────────────────────────────────────────────
// Hardened inputs
// ─────────────────────────────────────────────────────────────────────

/**
 * NameInput — strips digits as the user types and shows a friendly
 * validation message below when the input is invalid AND has been
 * touched (no scary red on first focus).
 */
export const NameInput = forwardRef(function NameInput({
  value, onChange, label = "name", placeholder, className = "",
  required = true, testid, autoFocus, disabled,
}, ref) {
  const handle = (e) => {
    // Live-strip digits + invalid chars so the user sees immediate feedback.
    const raw = e.target.value;
    const cleaned = raw.replace(/[\d@#$%^&*()_+={}\[\]|\\:;"<>?/!~`]+/g, "");
    onChange(cleaned);
  };
  const err = value && nameError(value, { required, label });
  return (
    <div className={className}>
      <input
        ref={ref}
        type="text"
        value={value || ""}
        onChange={handle}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        required={required}
        data-testid={testid}
        className={`input ${err ? "border-rose-400" : ""}`}
      />
      {err && (
        <div className="mt-1 text-xs text-rose-500" data-testid={testid ? `${testid}-error` : undefined}>
          {err}
        </div>
      )}
    </div>
  );
});

/**
 * NumberInput — digits-only with min/max + inline validation message.
 * Use for pincode, age, fee, salary. Use PhoneInput for phone numbers.
 */
export const NumberInput = forwardRef(function NumberInput({
  value, onChange, label = "value", placeholder, className = "",
  required = false, min, max, maxLength, testid, disabled,
}, ref) {
  const handle = (e) => {
    let cleaned = e.target.value.replace(/[^\d]/g, "");
    if (maxLength) cleaned = cleaned.slice(0, maxLength);
    onChange(cleaned);
  };
  const err = value !== undefined && value !== "" && numericError(value, { required, label, min, max });
  return (
    <div className={className}>
      <input
        ref={ref}
        type="text"
        inputMode="numeric"
        value={value ?? ""}
        onChange={handle}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        maxLength={maxLength}
        data-testid={testid}
        className={`input ${err ? "border-rose-400" : ""}`}
      />
      {err && (
        <div className="mt-1 text-xs text-rose-500" data-testid={testid ? `${testid}-error` : undefined}>
          {err}
        </div>
      )}
    </div>
  );
});
